import type { Organization } from '@core/models/organization';
import {
  DEFAULT_HIGH_COVERAGE_THRESHOLD,
  DEFAULT_LOW_COVERAGE_THRESHOLD,
} from '@hooks/queries/dashboard/dashboard.constants';
import {
  PLANET_VISUAL_BACKGROUND_MAX_AGE_DAYS,
  PLANET_VISUAL_BACKGROUND_MAX_CLOUD_PERCENT,
} from '@redux/imageAssets/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface ImageryFlags {
  FlagShowS2BAlert: boolean;
  FlagShowYieldAnalysis: boolean;
  FlagShowDataLayers: boolean;
  FlagShowInterrascanSoilMaps: boolean;
  FlagShowNdviReportDrawer: boolean;
  FlagShowTrialingPlots: boolean;
  FlagAllowRebrandRedirect: boolean;
  FlagReportAiSummary: boolean;
  FlagShowSystemNotifications: boolean;
  FlagShowMyCropwiseTopbar: boolean;
  FlagNewReportsDrawer: boolean;
  FlagFarmHighlightsOnboarding: boolean;
  FlagShowMapBiomas: boolean;
  FlagShowIntelligenceDashboard: boolean;
  FlagShowDashboardDetails: boolean;
  FlagShowDashboardHarvestData: boolean;
  FlagShowTimeSeriesPrecipitation: boolean;
  FlagTrialPlotsIndexAnalysis: boolean;
  FlagEnableCup4Soil: boolean;
  FlagEnableFarmHighlightsCupMetadata: boolean;
  FlagEnableFieldEditMfe: boolean;
  FlagPrescriptionFilenameInput: boolean;
  FlagBrazilBypassExcludedPlanIds: string;
  FlagPlanetVisualBackgroundMaxAgeDays: number;
  FlagPlanetVisualBackgroundMaxCloudPercent: number;
  FlagDashboardLowCoverageThreshold: number;
  FlagDashboardHighCoverageThreshold: number;
}

export const useImageryFlags = (organization?: Organization): ImageryFlags => {
  const launchdarklyFlags = useFlags<ImageryFlags>();
  const excludedPlanIds = (launchdarklyFlags.FlagBrazilBypassExcludedPlanIds ?? '')
    .split(',')
    .map((planId) => planId.trim())
    .filter(Boolean);
  const hasBypassExcludedPlan =
    !!organization?.planId && excludedPlanIds.includes(organization.planId);
  const enabledForBrazil = organization?.country === 'BR' && !hasBypassExcludedPlan;
  return {
    ...launchdarklyFlags,
    FlagShowS2BAlert: launchdarklyFlags.FlagShowS2BAlert ?? false,
    FlagShowInterrascanSoilMaps: launchdarklyFlags.FlagShowInterrascanSoilMaps ?? false,
    FlagPrescriptionFilenameInput: launchdarklyFlags.FlagPrescriptionFilenameInput ?? false,
    FlagShowDashboardDetails: launchdarklyFlags.FlagShowDashboardDetails ?? false,
    FlagShowDashboardHarvestData: launchdarklyFlags.FlagShowDashboardHarvestData ?? false,
    FlagShowYieldAnalysis: enabledForBrazil || launchdarklyFlags.FlagShowYieldAnalysis,
    FlagShowNdviReportDrawer: enabledForBrazil || launchdarklyFlags.FlagShowNdviReportDrawer,
    FlagShowDataLayers: enabledForBrazil || launchdarklyFlags.FlagShowDataLayers,
    FlagEnableCup4Soil: launchdarklyFlags.FlagEnableCup4Soil ?? false,
    FlagEnableFarmHighlightsCupMetadata:
      launchdarklyFlags.FlagEnableFarmHighlightsCupMetadata ?? false,
    FlagEnableFieldEditMfe: launchdarklyFlags.FlagEnableFieldEditMfe ?? false,
    FlagPlanetVisualBackgroundMaxAgeDays:
      launchdarklyFlags.FlagPlanetVisualBackgroundMaxAgeDays ??
      PLANET_VISUAL_BACKGROUND_MAX_AGE_DAYS,
    FlagPlanetVisualBackgroundMaxCloudPercent:
      launchdarklyFlags.FlagPlanetVisualBackgroundMaxCloudPercent ??
      PLANET_VISUAL_BACKGROUND_MAX_CLOUD_PERCENT,
    FlagDashboardLowCoverageThreshold:
      launchdarklyFlags.FlagDashboardLowCoverageThreshold ?? DEFAULT_LOW_COVERAGE_THRESHOLD,
    FlagDashboardHighCoverageThreshold:
      launchdarklyFlags.FlagDashboardHighCoverageThreshold ?? DEFAULT_HIGH_COVERAGE_THRESHOLD,
  };
};
